import { inject, Injector } from '@angular/core';
import {
	ModulesEnum,
	RentalAgreementRecordDto,
	RentalAgreementTypeEnum,
	TransactionRecordCategoryEnum,
	TransactionRecordDto,
} from '@splitsecondconnect/ultramarine-shared';

import { FEATURES } from '../../../../core/features/features';
import { ListComponent } from '../../../../shared/components/list/list.component';
import { ModuleSelectorStrategy } from '../../../../shared/models/module-selector.strategy';
import { LIST_STRATEGY_TOKEN } from '../../../../shared/tokens/list-strategy.token';
import { getRentalAgreementFilters } from '../../rental-agreements/config/rental-agreement-filters.config';
import { RentalAgreementListStrategy } from '../../rental-agreements/config/rental-agreement-list-strategy';
import { RentalAgreementsRecordComponent } from '../../rental-agreements/rental-agreements-record/rental-agreements-record.component';
import { RentalAgreementsRecordResolver } from '../../rental-agreements/rental-agreements-record/rental-agreements-record.resolver';
import { getTransactionFilters } from '../../transactions/config/transaction-filters.config';
import { TransactionListStrategy } from '../../transactions/config/transaction-list-strategy';
import { TransactionsRecordComponent } from '../../transactions/transactions-record/transactions-record.component';
import { TransactionsRecordResolver } from '../../transactions/transactions-record/transactions-record.resolver';
import { getVehicleDamageFilters } from '../../vehicle-damages/config/vehicle-damage-filters.config';
import { VehicleDamageListStrategy } from '../../vehicle-damages/config/vehicle-damage-list-strategy';

export class CustomerModuleSelectorStrategy implements ModuleSelectorStrategy {
	#injector = inject(Injector);
	originModule = ModulesEnum.CUSTOMERS;
	suggestTransaction = false;
	options = [
		{
			key: 'rental-agreements|title',
			svgIcon: 'receipt_long',
			class: 'col-span-2 md:col-span-1',
			value: ModulesEnum.RENTAL_AGREEMENTS,
			component: ListComponent,
			featureFlag: FEATURES.rentalAgreements,
			partialDataKey: 'customerId',
			hiddenFilterKey: 'customers',
			filters: getRentalAgreementFilters().filter(
				(item) => ![ModulesEnum.CUSTOMERS].includes(item.module),
			),
			injector: Injector.create({
				providers: [
					{
						provide: LIST_STRATEGY_TOKEN,
						useClass: RentalAgreementListStrategy,
					},
				],
				parent: this.#injector,
			}),
			newRecordComponent: RentalAgreementsRecordComponent,
			newRecordResolver: RentalAgreementsRecordResolver,
			newRecordPartialData: (id: number): Partial<RentalAgreementRecordDto> => {
				return {
					type: RentalAgreementTypeEnum.WALK_IN,
					customerId: id,
				};
			},
		},

		{
			key: 'transactions|title',
			svgIcon: 'payment',
			value: ModulesEnum.TRANSACTIONS,
			component: ListComponent,
			featureFlag: FEATURES.transactions,
			partialDataKey: 'customerId',
			hiddenFilterKey: 'customers',
			filters: getTransactionFilters().filter(
				(item) => ![ModulesEnum.CUSTOMERS].includes(item.module),
			),
			injector: Injector.create({
				providers: [
					{ provide: LIST_STRATEGY_TOKEN, useClass: TransactionListStrategy },
				],
				parent: this.#injector,
			}),
			newRecordComponent: TransactionsRecordComponent,
			newRecordResolver: TransactionsRecordResolver,
			newRecordPartialData: (id: number): Partial<TransactionRecordDto> => {
				return {
					recordCategory: TransactionRecordCategoryEnum.CUSTOMER,
					customerId: id,
				};
			},
		},
		{
			key: 'damages|title',
			svgIcon: 'bolt',
			value: ModulesEnum.VEHICLE_DAMAGES,
			component: ListComponent,
			featureFlag: FEATURES.vehicleDamages,
			partialDataKey: 'customerId',
			hiddenFilterKey: 'customers',
			filters: getVehicleDamageFilters().filter(
				(item) => ![ModulesEnum.CUSTOMERS].includes(item.module),
			),
			injector: Injector.create({
				providers: [
					{ provide: LIST_STRATEGY_TOKEN, useClass: VehicleDamageListStrategy },
				],
				parent: this.#injector,
			}),
		},
	];
}
